import { useEffect, useRef, useState } from "react"

export const TYPE_MS = 35
export const HOLD_MS = 1800
export const ERASE_MS = 16
export const GAP_MS = 350

const REDUCE = "(prefers-reduced-motion: reduce)"

/** True while the reader asks for less motion (tracks changes). */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => (typeof window !== "undefined" && window.matchMedia?.(REDUCE).matches) || false)
  useEffect(() => {
    const mq = window.matchMedia?.(REDUCE)
    if (!mq) return
    const on = () => setReduced(mq.matches)
    on()
    mq.addEventListener?.("change", on)
    return () => mq.removeEventListener?.("change", on)
  }, [])
  return reduced
}

/** The string typed so far, cycling through `lines` while `active`. Pauses while the tab is hidden; clears its timer on unmount. */
export function useTypedPlaceholder(lines: readonly string[], active: boolean): string {
  const [shown, setShown] = useState("")
  const idx = useRef(0)
  const [hidden, setHidden] = useState(() => typeof document !== "undefined" && document.hidden)

  useEffect(() => {
    const on = () => setHidden(document.hidden)
    document.addEventListener("visibilitychange", on)
    return () => document.removeEventListener("visibilitychange", on)
  }, [])

  const run = active && !hidden && lines.length > 0
  useEffect(() => {
    if (!run) return
    let timer = 0
    let n = 0
    const line = () => lines[idx.current % lines.length]
    const type = () => {
      n += 1
      setShown(line().slice(0, n))
      timer = window.setTimeout(n >= line().length ? erase : type, n >= line().length ? HOLD_MS : TYPE_MS)
    }
    const erase = () => {
      n -= 1
      setShown(line().slice(0, Math.max(n, 0)))
      if (n <= 0) {
        idx.current += 1
        timer = window.setTimeout(type, GAP_MS)
      } else timer = window.setTimeout(erase, ERASE_MS)
    }
    n = 0
    setShown("")
    timer = window.setTimeout(type, TYPE_MS)
    return () => window.clearTimeout(timer)
  }, [run, lines])

  return shown
}
